import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Chip, Tab, Tabs } from '@mui/material';
import { selectAccessibleFacilities } from '../../../store/selectors/shipmentModuleSelectors';
import { getStateDisplayName } from '../../../store/utils/shipmentUtils';
import { navigateToShipstationFacility } from '../../../store/thunks/shipstationShipmentThunks';

const TabLabel = ({ label, count, loading }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    {label}
    <Chip
      label={loading ? '...' : count}
      size='small'
      sx={{
        height: 18,
        fontSize: '0.7rem',
        minWidth: 24,
        '& .MuiChip-label': { px: 0.5 },
      }}
    />
  </Box>
);

TabLabel.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
};

const ShipstationFacilityTabs = () => {
  const dispatch = useDispatch();
  const { activeCategory, activeSubcategory, activeFacility, counts } = useSelector(
    ({ shipstationShipment }) => shipstationShipment
  );
  const facilities = useSelector(selectAccessibleFacilities);

  if (facilities.length === 0) return null;

  const handleChange = (_, newValue) => {
    dispatch(navigateToShipstationFacility({ pharmacyId: newValue }));
  };

  const getActiveCounts = () => {
    if (activeCategory === 'shipped') {
      if (activeSubcategory === 'all')
        return { total: counts.shipped.all, byPharmacy: counts.shipped.allByPharmacy };
      if (activeSubcategory === 'today')
        return { total: counts.shipped.today, byPharmacy: counts.shipped.todayByPharmacy };
      if (activeSubcategory === 'yesterday')
        return { total: counts.shipped.yesterday, byPharmacy: counts.shipped.yesterdayByPharmacy };
      if (activeSubcategory === 'voided')
        return { total: counts.shipped.voided, byPharmacy: counts.shipped.voidedByPharmacy };
      if (activeSubcategory === 'recent')
        return { total: counts.shipped.recent, byPharmacy: counts.shipped.recentByPharmacy };
      return { total: counts.shipped.last60, byPharmacy: counts.shipped.last60ByPharmacy };
    }
    // awaitingShipment
    if (activeSubcategory === 'recent')
      return { total: counts.awaiting.recent, byPharmacy: counts.awaiting.recentByPharmacy };
    return { total: counts.awaiting.total, byPharmacy: counts.awaiting.byPharmacy };
  };

  const activeCounts = getActiveCounts();
  const isShipped = activeCategory === 'shipped';

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
      <Tabs
        value={activeFacility}
        onChange={handleChange}
        variant='scrollable'
        scrollButtons='auto'
        sx={{
          minHeight: 36,
          '& .MuiTab-root': { minHeight: 36, py: 0, px: 1.5, textTransform: 'none' },
        }}
      >
        <Tab
          label={
            isShipped ? (
              'All Facilities'
            ) : (
              <TabLabel
                label='All Facilities'
                count={activeCounts.total}
                loading={counts.loading}
              />
            )
          }
          value='all'
        />
        {facilities.map((f) => {
          const facilityLabel = f.state ? `${f.name} (${getStateDisplayName(f.state)})` : f.name;
          return (
            <Tab
              key={f.fulfillingPharmacyId}
              label={
                isShipped ? (
                  facilityLabel
                ) : (
                  <TabLabel
                    label={facilityLabel}
                    count={activeCounts.byPharmacy[f.fulfillingPharmacyId] || 0}
                    loading={counts.loading}
                  />
                )
              }
              value={f.fulfillingPharmacyId}
            />
          );
        })}
      </Tabs>
    </Box>
  );
};

export default ShipstationFacilityTabs;
